import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '../ui/SectionTitle.js';
import { Field } from '../ui/Field.js';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { Pill } from '../PillGroup.js';
import { useStripeMode, useSetStripeMode } from '../../hooks/useStripeMode.js';

// ── Interruptor de modo de Stripe ────────────────────────────────────────────
// 2026-08-31. La instalación corrió tres meses en modo prueba sin que nadie lo
// supiera: una cuenta figuraba con suscripción activa de $150 al mes que nunca
// movió un peso. Esta tarjeta existe para que ese estado sea imposible de no
// ver, y para poder cambiarlo sin volver a pegar credenciales.
//
// Dos decisiones de diseño que importan:
//
//  - El modo se muestra SIEMPRE, en grande, esté como esté. No es un aviso que
//    aparece cuando algo anda mal: es el estado, permanente.
//  - Pasar a real pide confirmación y ENUMERA lo que se rompe. Las
//    suscripciones de prueba no existen en modo real; los clientes que ya
//    cargaron tarjeta tienen que volver a cargarla. Que aparezca después es un
//    llamado del cliente preguntando por qué le dejaron de cobrar.

export function StripeModeCard() {
  const { t } = useTranslation();
  const { data: estado, isLoading } = useStripeMode();
  const setMode = useSetStripeMode();
  const [confirmando, setConfirmando] = useState<'test' | 'live' | null>(null);

  if (isLoading || !estado) return null;

  const destino = estado.mode === 'test' ? 'live' : 'test';
  const tieneLlaveDestino = destino === 'live' ? estado.hasLiveKey : estado.hasTestKey;

  return (
    <div>
      <SectionTitle>{t('settings.stripeMode.title')}</SectionTitle>

      <Field label={t('settings.stripeMode.current')} hint={t('settings.stripeMode.currentHint')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', flexWrap: 'wrap' }}>
          <Pill tone={estado.isTest ? 'amber' : 'success'} size="md">
            {t(estado.isTest ? 'settings.stripeMode.test' : 'settings.stripeMode.live')}
          </Pill>

          <Button
            variant="outline"
            tone={destino === 'live' ? 'cyan' : 'muted'}
            size="sm"
            disabled={!tieneLlaveDestino}
            onClick={() => setConfirmando(destino)}
          >
            {t(destino === 'live' ? 'settings.stripeMode.switchToLive' : 'settings.stripeMode.switchToTest')}
          </Button>
        </div>
      </Field>

      {/* Sin la llave del destino el cambio dejaría el cobro caído — el
          servidor lo rechaza igual, esto explica por qué el botón no anda. */}
      {!tieneLlaveDestino && (
        <p style={{ margin: 'var(--evo-space-2) 0 0', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-text-muted)' }}>
          {t('settings.stripeMode.missingKey', { slot: `stripe-secret-key-${destino}` })}
        </p>
      )}

      {/* La llave cargada dice un modo y el interruptor dice otro. No se
          corrige solo: usar una llave que nadie pidió sería peor. */}
      {estado.mismatch && (
        <p style={{ margin: 'var(--evo-space-2) 0 0', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
          {t('settings.stripeMode.mismatch')}
        </p>
      )}

      {estado.usingLegacySlot && (
        <p style={{ margin: 'var(--evo-space-2) 0 0', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-text-muted)' }}>
          {t('settings.stripeMode.legacySlot')}
        </p>
      )}

      {confirmando && (
        <Modal
          open
          onClose={() => setConfirmando(null)}
          title={t(confirmando === 'live' ? 'settings.stripeMode.confirmLiveTitle' : 'settings.stripeMode.confirmTestTitle')}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', lineHeight: 1.6 }}>
              {t(confirmando === 'live' ? 'settings.stripeMode.confirmLiveIntro' : 'settings.stripeMode.confirmTestIntro')}
            </p>

            <ul style={{ margin: 0, paddingLeft: '1.2em', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', lineHeight: 1.7 }}>
              {(confirmando === 'live'
                ? ['settings.stripeMode.liveP1', 'settings.stripeMode.liveP2', 'settings.stripeMode.liveP3', 'settings.stripeMode.liveP4']
                : ['settings.stripeMode.testP1', 'settings.stripeMode.testP2']
              ).map(k => <li key={k}>{t(k)}</li>)}
            </ul>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-4)' }}>
            <Button variant="ghost" tone="muted" onClick={() => setConfirmando(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="solid"
              tone={confirmando === 'live' ? 'danger' : 'cyan'}
              loading={setMode.isPending}
              onClick={() => setMode.mutate(confirmando, { onSuccess: () => setConfirmando(null) })}
            >
              {t(confirmando === 'live' ? 'settings.stripeMode.confirmLiveAction' : 'settings.stripeMode.confirmTestAction')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
